import { ref, type Ref } from 'vue';

import { createSingleton } from '@deepidoo/deesplay-shared/utils';

export type Languages = 'de-DE' | 'fr-FR' | 'en-US';
type Keys = 'report-dialog.title' |
            'report.properties.status' |
            'report.properties.category' |
            'report.properties.title' |
            'report.properties.description' |
            'report.properties.page-url' |
            'report.properties.client' |
            'report.properties.user' |
            'report.properties.browser' |
            'report.properties.operating-system' |
            'report.properties.window-size' |
            'report.properties.created-at' |
            'report.category.bug' |
            'report.category.design' |
            'report.category.question' |
            'report.category.request' |
            'report.category.wording' |
            'report.status.closed' |
            'report.status.in-progress' |
            'report.status.non-reproductible' |
            'report.status.pending' |
            'report.status.resolved' |
            'report.status.wont-fix' |
            'report.conversation.title' |
            'report.conversation.placeholder';

const translations: Record<Languages, Record<Keys, string>> = {
    'en-US': {
        'report-dialog.title': 'Reports',
        'report.properties.status': 'Status',
        'report.properties.category': 'Category',
        'report.properties.title': 'Title',
        'report.properties.description': 'Description',
        'report.properties.page-url': 'Page URL',
        'report.properties.client': 'Client',
        'report.properties.user': 'User',
        'report.properties.browser': 'Browser',
        'report.properties.operating-system': 'Operating system',
        'report.properties.window-size': 'Window size',
        'report.properties.created-at': 'Created at',
        'report.category.bug': 'Bug',
        'report.category.design': 'Design',
        'report.category.question': 'Question',
        'report.category.request': 'Request',
        'report.category.wording': 'Wording',
        'report.status.closed': 'Closed',
        'report.status.in-progress': 'In progress',
        'report.status.non-reproductible': 'Non reproductible',
        'report.status.pending': 'Pending',
        'report.status.resolved': 'Resolved',
        'report.status.wont-fix': 'Wont fix',
        'report.conversation.title': 'Conversation',
        'report.conversation.placeholder': 'Enter a message (Press Shift + Enter for a new line)...'
    },
    'fr-FR': {
        'report-dialog.title': 'Rapports',
        'report.properties.status': 'Statut',
        'report.properties.category': 'Catégorie',
        'report.properties.title': 'Titre',
        'report.properties.description': 'Description',
        'report.properties.page-url': 'URL de la page',
        'report.properties.client': 'Client',
        'report.properties.user': 'Utilisateur',
        'report.properties.browser': 'Navigateur',
        'report.properties.operating-system': 'Système d\'exploitation',
        'report.properties.window-size': 'Taille de la fenêtre',
        'report.properties.created-at': 'Créé le',
        'report.category.bug': 'Bug',
        'report.category.design': 'Design',
        'report.category.question': 'Question',
        'report.category.request': 'Requête',
        'report.category.wording': 'Texte',
        'report.status.closed': 'Fermé',
        'report.status.in-progress': 'En cours',
        'report.status.non-reproductible': 'Non reproductible',
        'report.status.pending': 'En attente',
        'report.status.resolved': 'Résolu',
        'report.status.wont-fix': 'Refusé',
        'report.conversation.title': 'Conversation',
        'report.conversation.placeholder': 'Entrez un message (Appuyez sur Shift + Entrée pour une nouvelle ligne)...'
    },
    'de-DE': {
        'report-dialog.title': 'Berichte',
        'report.properties.status': 'Status',
        'report.properties.category': 'Kategorie',
        'report.properties.title': 'Titel',
        'report.properties.description': 'Beschreibung',
        'report.properties.page-url': 'Seiten-URL',
        'report.properties.client': 'Client',
        'report.properties.user': 'Benutzer',
        'report.properties.browser': 'Browser',
        'report.properties.operating-system': 'Betriebssystem',
        'report.properties.window-size': 'Fenstergröße',
        'report.properties.created-at': 'Erstellt am',
        'report.category.bug': 'Fehler',
        'report.category.design': 'Design',
        'report.category.question': 'Frage',
        'report.category.request': 'Anfrage',
        'report.category.wording': 'Schreibweise',
        'report.status.closed': 'Geschlossen',
        'report.status.in-progress': 'In Arbeit',
        'report.status.non-reproductible': 'Nicht reproduzierbar',
        'report.status.pending': 'Ausstehend',
        'report.status.resolved': 'Gelöst',
        'report.status.wont-fix': 'Nicht behebbar',
        'report.conversation.title': 'Konversation',
        'report.conversation.placeholder': 'Geben Sie eine Nachricht ein (Drücken Sie Shift + Eingabe für eine neue Zeile)...'
    }
};

export const useTranslations = createSingleton((): {
    languageCode: Ref<Languages>;
    tr: (key: Keys) => string;
} => {
    const languageCode = ref<Languages>('en-US');

    const tr = (key: Keys): string => {
        return translations[languageCode.value][key] || key;
    };

    return {
        languageCode,
        tr
    };
});